import Dexie, { type Table } from 'dexie'
import type { CommandStatus, CommandWire } from '@gk/contracts'

/** Device-side store (ARCHITECTURE 9.1): a mirror of what the server let this device cache, the outbox of commands not
 *  yet confirmed, an overlay (local projection of pending commands), an archive of applied commands, and sync metadata.
 *  One database per (auth user, org): a login that works for two clubs keeps two stores, so cursors and ids never mix. */

export interface OutboxRow extends CommandWire {
  local_seq: number
  status: CommandStatus
  attempts: number
  timeouts: number
  next_attempt_at?: number          // epoch ms: do not (re)send before this
  last_attempt_at?: string
  server_code?: string | null
  server_result?: unknown
  exceptions?: string[]
  intent_key: string                // double-tap guard (9.14): same entity + action + local revision is a no-op
  auth_mode: 'personal' | 'kiosk'
  device_id: string
  app_version: string
  created_at: string
  /** device-only, never sent (`toWire` picks the wire fields): an id the screen that queued the command needs to recognise its row later. A position
   *  carries the running interval it was taken in, so that a refusal stops the sharing for that stretch of work only (data/livePosition.ts). */
  local_tag?: string
}
export interface MetaRow { key: string; value: unknown }
/** A downloaded map archive (not mirrored from the server: the bytes come from private storage, verified against the published hash). */
export interface MapFileRow { key: string; layer_id: string; sha256: string; bytes: number; blob: Blob; saved_at: string }
/** A photo taken on this device, kept until the server has it: registered (the register command may still be waiting), uploaded (the bytes are in storage, the confirm may still be waiting) or failed (a refusal
 *  the person should see). Keyed by the hash, so the same picture is one row however many records it is evidence for. Not mirrored from the server; the bytes go once the server says stored. */
export interface BlobRow {
  sha256: string; bytes: number; mime: string; blob: Blob; file_id: string; storage_key: string
  register_command_ids: string[]; confirm_command_id?: string
  state: 'registered' | 'uploaded' | 'failed'
  attempts: number; last_error?: string; created_at: string
  /** M15-V: the bucket the bytes go to (a PDF or a vault file: 'documents'); absent = the photos' bucket. A field of a store that is not indexed: no bump. */
  bucket?: 'attachments' | 'documents'
  /** Release b review (finding 31): every command that names these bytes registers them on a SENSITIVE record (an incident's evidence, a cost's invoice): once the
   *  lane gives them up (refused, or the upload failed for good) they are deleted, never kept as 'failed' (the reporter keeps nothing of an incident). Not indexed. */
  sensitive?: true
}
export interface ArchiveRow { command_id: string; name: string; aggregate_id?: string; applied_at: string; epoch: string; local_seq: number }
/** A local projection of a pending command. `insert`: a row that does not exist on the server yet (hidden once the server's own row
 *  arrives). `patch`: fields laid OVER the server's row until the command is resolved (an offline edit must show at once, but the
 *  server's row stays the truth). */
export interface OverlayRow { key: string; table: string; id: string; row: Record<string, unknown>; command_id: string; op?: 'insert' | 'patch' }
type Mirror = Table<Record<string, unknown> & { id: string }, string>

/** Server table -> local store. Only tables in the personal sync profile (sync.table_profile) can appear here. */
export const MIRROR_TABLES: Record<string, string> = {
  'core.site': 'site',
  'core.person': 'person',
  'core.membership': 'membership',
  'core.role': 'role',
  'core.permission': 'permission',
  'core.role_permission': 'role_permission',
  'fleet.asset': 'asset',
  'fleet.asset_type_local': 'asset_type',
  'fleet.mount_interval': 'mount',
  'fleet.external_identity': 'external_identity',
  'fleet.asset_capability': 'capability',
  'fleet.meter_slot': 'meter_slot',
  'fleet.meter_instance': 'meter_instance',
  'fleet.meter_observation': 'meter_observation',
  'fleet.service_plan': 'service_plan',
  'fleet.service_plan_version': 'service_plan_version',
  'fleet.service_event': 'service_event',
  'fleet.checklist_template': 'checklist_template',
  'fleet.checklist_version': 'checklist_version',
  'fleet.checklist_item': 'checklist_item',
  'fleet.inspection': 'inspection',
  'fleet.defect': 'defect',
  'fleet.defect_event': 'defect_event',
  'fleet.asset_restriction': 'restriction',
  'fleet.asset_restriction_release': 'restriction_release',
  'fleet.work_order': 'work_order',
  'fleet.work_order_defect': 'work_order_defect',
  'geo.area': 'area',
  'geo.area_version': 'area_version',
  'geo.infra_feature': 'infra_feature',
  'geo.infra_feature_version': 'infra_version',
  'geo.map_layer': 'map_layer',
  'ops.task_type': 'task_type',
  'core.document': 'document',
  'core.document_version': 'document_version',
  'ops.job_template': 'job_template',
  'ops.job_template_version': 'job_template_version',
  'ops.job': 'job',
  'ops.job_area': 'job_area',
  'ops.assignment': 'assignment',
  'ops.work_session': 'work_session',
  'ops.work_interval': 'work_interval',
  'ops.job_completion': 'job_completion',
  'ops.job_area_coverage': 'job_area_coverage',
  'core.attachment_link': 'attachment_link',
  'catalog.supplier': 'supplier',
  'catalog.product': 'product',
  'catalog.product_version': 'product_version',
  'catalog.batch': 'batch',
  'fleet.work_order_part': 'work_order_part',
  'ops.measurement': 'measurement',
  'ops.measurement_target': 'measurement_target',
  'ops.irrigation_run': 'irrigation_run',
  'ops.irrigation_fault': 'irrigation_fault',
  'ops.water_profile': 'water_profile',
  // M17: the club's look
  'core.club_branding': 'club_branding',
  // M20: the staff register (a person's fonction and the days they work here; the board and the week grid show them)
  'hr.job_title': 'job_title',
  'hr.employment': 'employment',
  // M16a (release a, docs/M16.md; the seven stores of Dexie v13, each table created by its part's migration): the notices to the team (B1), the flags
  // (C: placements, exclusions, the club's sector words), a ground observation (N), the measured cutting height (J), the events on an irrigation fault (L1)
  'ops.course_notice': 'course_notice',
  'ops.pin_placement': 'pin_placement',
  'ops.pin_exclusion': 'pin_exclusion',
  'ops.flag_settings': 'flag_settings',
  'ops.observation': 'observation',
  'fleet.cutting_setup': 'cutting_setup',
  'ops.irrigation_fault_event': 'irrigation_fault_event',
  // M15a (release a, docs/M15.md; the five stores of Dexie v14, each table created by its part's migration): the stock ledger (A: the places and the movements, the
  // fill-ups among them), the treatments (C: the plan on a job, the application consigned with the register's fields, the areas it covered)
  'stock.location': 'stock_location',
  'stock.movement': 'stock_movement',
  'agro.treatment_plan': 'treatment_plan',
  'agro.application': 'application',
  'agro.application_area': 'application_area',
  // Release b (M16b and M15b, docs/M16.md rule 3 and docs/M15.md rule 9; the four mirrored stores of Dexie v15, each table created by its part's migration): what
  // was done on an infrastructure feature (M16-E), who read a procedure version (M16-I2), the physical count and its lines (M15-B)
  'geo.infra_maintenance': 'infra_maintenance',
  'ops.procedure_acknowledgement': 'procedure_acknowledgement',
  'stock.count': 'stock_count',
  'stock.count_line': 'stock_count_line',
  // M25 (docs/M25.md rule 8 and B; the two stores of Dexie v16, Z's opening; each table created by its part's migration): the settings a person confirmed at the
  // finish (F: ops.job_settings_confirmation, a field fact; both profiles, a crew phone holding its own) and the club's correction of a line of play (L:
  // geo.area_play_bearing, an office intent; both profiles). Until a table exists on the server, nothing arrives for its store.
  'ops.job_settings_confirmation': 'job_settings_confirmation',
  'geo.area_play_bearing': 'area_play_bearing',
}
export const MIRROR_STORES = Object.values(MIRROR_TABLES)

/** Stores a command writes on THIS device only (release b, the b opening). Not mirrored (no server table feeds them: MIRROR_TABLES never names them), never
 *  rewound (a resnapshot clears the mirror stores alone, and a later version's upgrade only rewrites the cursor), never touched by a tombstone. Every
 *  transaction that queues a command (`enqueue`) or records the server's answer to one (the engine's push and pull, the discard paths) includes them
 *  (`localOnlyTables`): a part's projector writes its row in the command's own transaction, and its cleanup runs where the answer is recorded
 *  (`settleTransient`, sync/transient.ts). `incident_stub` (M16-G): an incident report sent from this device, WITHOUT its text, its people or its place
 *  (an incident is kept on no device: ADR-119); G's projector writes it, G's cleanup removes it once the server has answered. */
export const LOCAL_ONLY_STORES = ['incident_stub'] as const
/** What this device keeps of an incident it reported (M16-G): the id, the kind, the command that carries it, when; never a text, a person or a place. */
export interface IncidentStubRow { id: string; kind: string; command_id: string; created_at: string }
export const localOnlyTables = (db: GkDb) => LOCAL_ONLY_STORES.map((s) => db.table(s))

export class GkDb extends Dexie {
  meta!: Table<MetaRow, string>
  outbox!: Table<OutboxRow, string>
  archive!: Table<ArchiveRow, string>
  overlay!: Table<OverlayRow, string>
  site!: Mirror; person!: Mirror; membership!: Mirror; role!: Mirror; permission!: Mirror; role_permission!: Mirror; asset!: Mirror
  asset_type!: Mirror; mount!: Mirror; external_identity!: Mirror; capability!: Mirror
  meter_slot!: Mirror; meter_instance!: Mirror; meter_observation!: Mirror; service_plan!: Mirror; service_plan_version!: Mirror; service_event!: Mirror
  checklist_template!: Mirror; checklist_version!: Mirror; checklist_item!: Mirror; inspection!: Mirror; defect!: Mirror; defect_event!: Mirror
  restriction!: Mirror; restriction_release!: Mirror; work_order!: Mirror; work_order_defect!: Mirror
  area!: Mirror; area_version!: Mirror; infra_feature!: Mirror; infra_version!: Mirror; map_layer!: Mirror
  task_type!: Mirror; document!: Mirror; document_version!: Mirror; job_template!: Mirror; job_template_version!: Mirror; job!: Mirror; job_area!: Mirror; assignment!: Mirror
  work_session!: Mirror; work_interval!: Mirror; job_completion!: Mirror; job_area_coverage!: Mirror
  map_file!: Table<MapFileRow, string>
  attachment_link!: Mirror
  supplier!: Mirror; product!: Mirror; product_version!: Mirror; batch!: Mirror; work_order_part!: Mirror
  measurement!: Mirror; measurement_target!: Mirror
  irrigation_run!: Mirror; irrigation_fault!: Mirror; water_profile!: Mirror
  club_branding!: Mirror
  job_title!: Mirror; employment!: Mirror
  course_notice!: Mirror; pin_placement!: Mirror; pin_exclusion!: Mirror; flag_settings!: Mirror; observation!: Mirror; cutting_setup!: Mirror; irrigation_fault_event!: Mirror
  stock_location!: Mirror; stock_movement!: Mirror; treatment_plan!: Mirror; application!: Mirror; application_area!: Mirror
  infra_maintenance!: Mirror; procedure_acknowledgement!: Mirror; stock_count!: Mirror; stock_count_line!: Mirror
  job_settings_confirmation!: Mirror; area_play_bearing!: Mirror
  incident_stub!: Table<IncidentStubRow, string>
  blobs!: Table<BlobRow, string>

  constructor(name: string) {
    super(name)
    this.version(1).stores({
      meta: 'key',
      outbox: 'command_id, local_seq, status',
      archive: 'command_id, applied_at',
      overlay: 'key, command_id, table',
      site: 'id', person: 'id', membership: 'id', role: 'id', permission: 'id', role_permission: 'id',
      asset: 'id, readable_tag',
    })
    // v2 (M2): types, mount history, vendor id mappings, declared capabilities
    this.version(2).stores({
      asset_type: 'id, code',
      mount: 'id, component_asset_id, host_asset_id',
      external_identity: 'id, entity_id',
      capability: 'id, asset_id',
    })
    // v3 (M3): meters (slots, counters, readings) and service plans; the readings are the only thing a device writes offline
    this.version(3).stores({
      meter_slot: 'id, asset_id',
      meter_instance: 'id, slot_id',
      meter_observation: 'id, meter_instance_id, observed_at',
      service_plan: 'id',
      service_plan_version: 'id, plan_id',
      service_event: 'id, asset_id',
    }).upgrade(async (tx) => {
      // New mirrored stores are empty, but the sync cursor still says "I have everything up to here": rows of the new tables created
      // before that cursor would NEVER arrive. Rewind the cursor so the next pull is a full resnapshot (idempotent; the outbox is untouched).
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])
    })
    // v4 (M4): the safety loop. Checklists, inspections, defects, restrictions (and their releases) and work orders. A pre-start check is
    // a safety FACT and works offline: it writes an overlay restriction at once, so a machine shows "out of service" on this device.
    this.version(4).stores({
      checklist_template: 'id', checklist_version: 'id, template_id', checklist_item: 'id, version_id',
      inspection: 'id, asset_id', defect: 'id, asset_id', defect_event: 'id, defect_id',
      restriction: 'id, asset_id', restriction_release: 'id, restriction_id', work_order: 'id, asset_id', work_order_defect: 'id, work_order_id, defect_id',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3: resnapshot
    })
    // v5 (M6): the course and the map. Areas and their shapes, infrastructure and its positions, the published map layers; and the one store that
    // is NOT mirrored: the downloaded map archive, kept as a Blob so the map opens with no network.
    this.version(5).stores({
      area: 'id, parent_id', area_version: 'id, area_id', infra_feature: 'id, area_id', infra_version: 'id, feature_id', map_layer: 'id',
      map_file: 'key, layer_id',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 and v4: resnapshot
    })
    // v6 (M7): planning. Task types, procedures (documents and their versions), job templates, jobs with their areas, and who is on them. All of it
    // is READ offline (the board, a job card, a procedure); it is written online by the planner.
    this.version(6).stores({
      task_type: 'id, code', document: 'id', document_version: 'id, document_id', job_template: 'id', job_template_version: 'id, template_id',
      job: 'id, scheduled_local_date', job_area: 'id, job_id', assignment: 'id, job_id, person_id',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v5: resnapshot
    })
    // v7 (M8): doing the work. Sessions, their timer intervals, completions and what each covered: the person's own on a phone, what is open on a shared workstation, the team's for
    // those who review time. Written OFFLINE (facts): the overlay shows a running timer at once. Photos: the links to the records they are evidence for (mirrored), and the pictures taken here that
    // the server has not stored yet (`blobs`, not mirrored, kept until stored).
    this.version(7).stores({
      work_session: 'id, job_id, person_id', work_interval: 'id, work_session_id, person_id', job_completion: 'id, job_id', job_area_coverage: 'id, completion_id',
      attachment_link: 'id, entity_id', blobs: 'sha256, state',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v6: resnapshot
    })
    // v8 (M9): the catalogue. Suppliers, products, their immutable versions (the density a conversion needs), batches, and the parts used on work orders. Read on every device (a part is
    // chosen from the local copy); written online, in the workshop.
    this.version(8).stores({
      supplier: 'id', product: 'id', product_version: 'id, product_id', batch: 'id, product_id', work_order_part: 'id, work_order_id',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v7: resnapshot
    })
    // v9 (M13): the measurement notebook and the club's targets. A reading is a field FACT (written offline, shown at once); the targets are read everywhere and set in the office.
    this.version(9).stores({
      measurement: 'id, measurement_type, area_id, observed_local_date', measurement_target: 'id, measurement_type',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v8: resnapshot
    })
    // v10 (M14): water and weather, read-only. What was irrigated and what went wrong (both written offline in the field), and the club's parameters for the advice (read everywhere, set in the office).
    this.version(10).stores({
      irrigation_run: 'id, area_id, started_local_date', irrigation_fault: 'id, area_id', water_profile: 'id',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v9: resnapshot
    })
    // M17: the club's look (its logo)
    this.version(11).stores({ club_branding: 'id' }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v10: resnapshot
    })
    // v12 (M20): the staff register. The club's fonctions and the employment periods (fonction and site-local days) of its people; read everywhere, written online by the direction.
    this.version(12).stores({ job_title: 'id, code', employment: 'id, person_id' }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v11: resnapshot
    })
    // v13 (M16a, the one bump of the release; docs/M16.md rule 3): what the Superintendent opens every morning. The notices to the team (read everywhere, written
    // online by whoever runs the course), the flags (a placement is a field FACT from the running job; the exclusions and the club's sector words are the office's),
    // a ground observation with its photo (a fact), the measured cutting height of a unit (a fact), and who saw, took and linked an irrigation fault (the office's).
    // The resnapshot also brings the new column of a mirrored store (ops.task_type.tags), which needs no bump of its own.
    this.version(13).stores({
      course_notice: 'id, status, starts_on', pin_placement: 'id, area_id, placed_on, job_id', pin_exclusion: 'id, area_id', flag_settings: 'id',
      observation: 'id, area_id, observed_at, job_id', cutting_setup: 'id, asset_id, measured_at', irrigation_fault_event: 'id, fault_id',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v12: resnapshot
    })
    // v14 (M15a, the one bump of the release; docs/M15.md rule 9): the shed's daily facts. The stock ledger (A: the places — the store, the shed, the fuel tank — and every
    // movement: a receipt, what a job used (a FACT typed once at the finish), a part on an order, a fill-up at the pump (a fact on the machine), a return, waste, a reversal,
    // a count adjustment) and the treatments (C: the plan verified on a job, the application consigned in the field with the French register's fields (a FACT), the areas it
    // covered with their surface). Read everywhere from the mirror (on-hand is a sum over the movements, the register is read per year); the facts are projected offline.
    // The resnapshot also brings the new columns of mirrored stores (catalog.product.min_qty_base, hr.qualification_type.kind), which need no bump of their own.
    // The b stores (stock_count, stock_count_line) are M16-Z's v15 (below).
    this.version(14).stores({
      stock_location: 'id, site_id', stock_movement: 'id, product_id, location_id, asset_id, job_id, work_session_id, application_id',
      treatment_plan: 'id, job_id, status', application: 'id, job_id, work_session_id, started_at', application_area: 'id, application_id, area_id',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v13: resnapshot
    })
    // v15 (release b of M16 and M15, the one bump of both; docs/M16.md rule 3, docs/M15.md rule 9; M16's parts before M15's): the rare registers. What was done on
    // an infrastructure feature (M16-E: repaired, replaced, flushed, inspected; a FACT a person records on the map), who read a procedure version (M16-I2: "J'ai lu
    // cette version", a fact), the physical count and its lines (M15-B: a count opened by the direction, each line a fact typed in the storeroom with no signal).
    // Read everywhere from the mirror, the facts projected offline. And ONE store that is NOT mirrored: `incident_stub` (LOCAL_ONLY_STORES, above), what this
    // device keeps of an incident it reported, without the text. No irrigation_programme store: M16-L2 waits for the owner (question 6) and brings its own bump.
    // The resnapshot also brings the new columns of mirrored stores (ops.measurement.position_label, core.document.ask_reading), which need no bump of their own.
    this.version(15).stores({
      infra_maintenance: 'id, feature_id, done_on', procedure_acknowledgement: 'id, person_id, document_version_id',
      stock_count: 'id, location_id, status', stock_count_line: 'id, count_id, product_id',
      incident_stub: 'id',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v14: resnapshot (the stub store is new and empty)
    })
    // v16 (M25, the one bump of the milestone; docs/M25.md rule 8, B and C; Z's opening): the settings in pictures. The settings a person confirmed at the end of a
    // job (F: one row per finish, `v5(work_session_id, 'settings')`, a FACT projected offline; a crew phone holds its own, the direction all, the workstation none)
    // and the club's correction of an area's line of play (L: a ledger, the newest per area in force; used only to turn a drawing). Read from the mirror; no
    // local-only store. The resnapshot also brings the new column of a mirrored store (ops.task_type.choice_looks: the pictures of the choices), which needs no
    // bump of its own.
    this.version(16).stores({
      job_settings_confirmation: 'id, work_session_id, job_id', area_play_bearing: 'id, area_id',
    }).upgrade(async (tx) => {
      await tx.table('meta').bulkPut([{ key: 'cursor', value: 0 }, { key: 'knownSeq', value: 0 }])      // same rule as v3 to v15: resnapshot
    })
  }
  mirrorStore(serverTable: string): Mirror | undefined {
    const s = MIRROR_TABLES[serverTable]
    return s ? (this.table(s) as Mirror) : undefined
  }
}

export const dbName = (authUserId: string, orgId: string) => `gk:${authUserId}:${orgId}`

/** The double-tap guard's key (ARCHITECTURE 9.14) when the caller gives none: the command, its entity and its payload (for a command of sync/transient.ts, its ids
 *  only: `keyedPayload`). */
export const intentKeyOf = (key: string, aggregateId: string | null | undefined, payload: unknown): string => `${key}|${aggregateId ?? ''}|${JSON.stringify(payload)}`

export async function getMeta<T>(db: GkDb, key: string): Promise<T | undefined> {
  return (await db.meta.get(key))?.value as T | undefined
}
export async function setMeta(db: GkDb, key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value })
}
